import { HttpErrorResponse } from '@angular/common/http';
import { AfterContentInit, Component, Inject, Input, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Account, PermittedOperationCurrencyType } from '@nano/account/common';
import { AlertService } from '@nano/common/alert';
import { AmountService } from '@nano/common/amount';
import { Classifier } from '@nano/common/classifier';
import { FormService } from '@nano/common/form';
import { CustomInputNumberParser, indicateLoading } from '@nano/common/util';
import { ErrorService } from '@nano/core/error';
import { TfaUsage } from '@nano/core/tfa';
import { Payment, PaymentConditions, PaymentConfig, PaymentParam, PaymentType } from '@nano/payment/payment';
import { PaymentService } from '@nano/payment/payment.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { catchError, combineLatest, EMPTY, merge, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, map, switchMap, take } from 'rxjs/operators';
import { CurrencyExchangeControls } from './currency-exchange';

@UntilDestroy()
@Component({
  selector: 'app-currency-exchange',
  templateUrl: './currency-exchange.component.html',
  styleUrls: ['./currency-exchange.component.less'],
  standalone: false
})
export class CurrencyExchangeComponent implements AfterContentInit, OnDestroy {
  @Input() payerAccount: Account;

  readonly CustomInputNumberParser = CustomInputNumberParser;

  readonly tfaUsage = TfaUsage.whenConfirmingOperation;
  readonly config: PaymentConfig = this.paymentService.config;

  currencies: Classifier[];

  form: UntypedFormGroup = new UntypedFormGroup({});
  submitButtonDisabled$: Subject<boolean> = new Subject();
  submitButtonLoading$: Subject<boolean> = new Subject();

  private valueChangesSubscription: Subscription;
  private amountFocusState = {
    initialAmount: false,
    amount: false
  };

  paymentConditions: PaymentConditions;

  get operationType(): PaymentType {
    return this.form.get(PaymentParam.operationType)?.value as PaymentType;
  }

  get beneficiaryAccountNumberControl(): UntypedFormControl {
    return this.form.get(PaymentParam.beneficiaryAccountNumber) as UntypedFormControl;
  }

  get beneficiaryAccountNumber(): string {
    return this.beneficiaryAccountNumberControl.value as string;
  }

  get payerAccountControl(): UntypedFormControl {
    return this.form.get(PaymentParam.payerAccount) as UntypedFormControl;
  }

  get payerAccountNumberControl(): UntypedFormControl {
    return this.form.get(PaymentParam.payerAccountNumber) as UntypedFormControl;
  }

  get initialAmountControl(): UntypedFormControl {
    return this.form.get(PaymentParam.initialAmount) as UntypedFormControl;
  }

  get initialCurrencyControl(): UntypedFormControl {
    return this.form.get(PaymentParam.initialCurrency) as UntypedFormControl;
  }

  get initialAmount(): number {
    return this.initialAmountControl?.value as number;
  }

  get initialCurrency(): string {
    return this.initialCurrencyControl?.value as string;
  }

  get amountControl(): UntypedFormControl {
    return this.form.get(PaymentParam.amount) as UntypedFormControl;
  }

  get amount(): number {
    return this.amountControl?.value as number;
  }

  get currencyControl(): UntypedFormControl {
    return this.form.get(PaymentParam.currency) as UntypedFormControl;
  }

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private formService: FormService,
    private paymentService: PaymentService,
    private errorService: ErrorService,
    private alertService: AlertService,
    private amountService: AmountService
  ) {
    this.formService.registerFormControls(this.form, CurrencyExchangeControls(this.config));

    this.payerAccountControl.valueChanges.pipe(untilDestroyed(this)).subscribe((account: Account) => {
      this.currencies = account?.permittedOperationCurrencies
        .get(PermittedOperationCurrencyType.CURRENCY_EXCHANGE)
        ?.filter((currency) => currency.id !== account.currency);
      this.beneficiaryAccountNumberControl.setValue(account.iban);
      this.payerAccountNumberControl.setValue(account.iban);
      this.initialCurrencyControl.setValue(account.currency);
    });

    const calcByInitialAmount$: Observable<Payment> = combineLatest([
      this.payerAccountControl.valueChanges,
      this.initialAmountControl.valueChanges,
      this.currencyControl.valueChanges
    ]).pipe(map(([payerAccount, , currency]) => this.createPaymentConditionsRequest(payerAccount as Account, currency as string, true)));

    const calcByAmount$: Observable<Payment> = combineLatest([
      this.payerAccountControl.valueChanges,
      this.amountControl.valueChanges,
      this.currencyControl.valueChanges
    ]).pipe(map(([payerAccount, , currency]) => this.createPaymentConditionsRequest(payerAccount as Account, currency as string, false)));

    merge(calcByAmount$, calcByInitialAmount$)
      .pipe(
        untilDestroyed(this),
        debounceTime(500),
        switchMap((payment: Payment) =>
          this.paymentService.getPaymentConditions(payment).pipe(
            indicateLoading(this.submitButtonDisabled$),
            catchError((error: unknown) => {
              this.errorService.handleHttpError(error as HttpErrorResponse);
              return EMPTY;
            })
          )
        )
      )
      .subscribe((paymentConditions) => {
        this.paymentConditions = { ...paymentConditions };
        this.syncAmountsFromConditions(paymentConditions);
      });

    this.valueChangesSubscription = this.form.valueChanges.subscribe(() => {
      this.alertService.cleanUp();
    });
  }

  handleKeyDown(event: KeyboardEvent): void {
    this.amountService.handleKeyDown(event);
  }

  onAmountFocusChange(control: 'initialAmount' | 'amount', focused: boolean): void {
    this.amountFocusState[control] = focused;
  }

  ngOnDestroy(): void {
    if (this.valueChangesSubscription) {
      this.alertService.cleanUp();
      this.valueChangesSubscription.unsubscribe();
    }
  }

  ngAfterContentInit(): void {
    if (this.payerAccount) {
      this.payerAccountControl.setValue(this.payerAccount);
    }
    this.paymentService.paymentSummary$.pipe(take(1)).subscribe((payment) => {
      if (payment?.beneficiaryAccountNumber) {
        this.form.patchValue(payment);
      }
    });
  }

  makePayment(): void {
    this.paymentService
      .createPaymentOperation(this.form.value as Payment, this.paymentConditions)
      .pipe(indicateLoading(this.submitButtonLoading$, { stopOnErrorOnly: true }))
      .subscribe();
  }

  private createPaymentConditionsRequest(payerAccount: Account, currency: string, isInitialAmountControl: boolean): Payment {
    this.submitButtonDisabled$.next(true);

    const shouldUseInitialAmount = (isInitialAmountControl && this.initialAmount) || (!isInitialAmountControl && !this.amount);
    const errorControl = shouldUseInitialAmount ? this.amountControl : this.initialAmountControl;
    errorControl.setErrors(null);

    return {
      operationType: this.operationType,
      beneficiaryAccountNumber: this.beneficiaryAccountNumber,
      payerAccount: payerAccount,
      payerAccountNumber: payerAccount.iban,
      initialAmount: shouldUseInitialAmount ? this.initialAmount : null,
      initialCurrency: this.initialCurrency,
      amount: shouldUseInitialAmount ? null : this.amount,
      currency: currency
    } as Payment;
  }

  private patchConditionsAmountControl(control: UntypedFormControl, value: number | null | undefined, isFocused: boolean): void {
    if (value == null || isFocused) {
      return;
    }

    control.patchValue(value, { emitEvent: false });
  }

  private syncAmountsFromConditions(paymentConditions: PaymentConditions): void {
    this.patchConditionsAmountControl(this.initialAmountControl, paymentConditions.initialAmount, this.amountFocusState.initialAmount);
    this.patchConditionsAmountControl(this.amountControl, paymentConditions.amount, this.amountFocusState.amount);
  }
}
